import React, { useState, useEffect } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { translate } from '../../../services/translate';
import DeleteModal from '../../../components/DeleteModal';
import Toast from 'react-native-toast-message';
import { Pagination } from '../../../components/Pagination';
import { accessService } from '../../../services/central-api/access';
import { ClientError } from '../../../services/central-api/base-service';
import { AccessParamList } from '../../../typings/Params';
import { NavigationProp, RouteProp, useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import MyDataTable from '../../../components/MyDataTable';
import FilterInput from '../../../components/Filter/components/FilterInput';
import CustomButton from '../../../components/CustomButton';
import useFilters from '../../../components/Filter/hooks/useFilters';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import DropdownButton from '../../../components/DropdownButton';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/Auth';

export default function ActingBodies() {
    const navigation = useNavigation<NavigationProp<AccessParamList>>();
    const route = useRoute<RouteProp<AccessParamList, 'ActingBodies'>>();
    const { user: authenticatedUser } = useAuth();

    const [filters, setFilters] = useFilters<AccessParamList['ActingBodies']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });

    const [selectedActingBody, setSelectedActingBody] = useState<ActingBody>();
    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
    const [actingBodies, setActingBodies] = useState<ActingBody[]>([]);
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [totalItems, setTotalItems] = useState(0);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['accessActingBodies', filters],
        queryFn: () => accessService.getPaginatedActingBodies(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    useEffect(() => {
        if (!data) {
            return;
        }
        setActingBodies(data.rows);
        setTotalItems(data.count);
    }, [data]);

    function handleEditClick(actingBody: ActingBody) {
        setSelectedActingBody(actingBody);
        navigation.navigate('ActingBodiesForm', { id: actingBody.id });
    }

    function showDeleteConfirmationDialog(actingBody: ActingBody) {
        setSelectedActingBody(actingBody);
        setDeleteModalVisible(true);
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));

            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
        }
    }

    async function confirmDelete(actingBody: ActingBody) {
        try {
            await accessService.deleteActingBody(actingBody.id);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

            setDeleteModalVisible(false);
            refetch();
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return <Container>
        <Header
            title={translate('actingBodies')}
            actions={<CustomButton text={translate('register')} onPress={() => {
                setSelectedActingBody(undefined);
                navigation.navigate('ActingBodiesForm');
            }} />}
        />
        <Card>
            <DataTableHeader
                zIndex={2}
                title={translate('registeredActingBodies')}
                filterInput={<FilterInput
                    value={textFilter}
                    placeholder={translate('typeToSearch')}
                    onChange={text => {
                        setTextFilter(text);
                    }}
                    onKeyPress={handleKeyDown}
                />}
            />
            <DataTableContainer
                dataTable={<MyDataTable
                    columns={[{
                        name: translate('name'),
                        selector: row => row.name,
                        wrap: true,
                        grow: 3
                    },
                    {
                        name: translate('userCount'),
                        selector: row => row.userCount || 0,
                        wrap: true,
                        grow: 1,
                        center: true,
                    },
                    {
                        name: translate('equipmentCount'),
                        selector: row => row.equipmentCount || 0,
                        wrap: true,
                        grow: 1,
                        center: true,
                    },
                    {
                        button: true,
                        cell: row => <DropdownButton
                            actions={[{
                                content: translate('editInformation'),
                                onPress: () => {
                                    handleEditClick(row);
                                }
                            }, {
                                content: translate('delete'),
                                variant: 'danger',
                                onPress: () => {
                                    showDeleteConfirmationDialog(row);
                                },
                            }]}
                        />
                    }]}
                    data={actingBodies}
                    progressPending={isLoading}
                />}
                pagination={<Pagination
                    totalItems={totalItems}
                    currentPage={filters.page}
                    pageSize={filters.limit}
                    setPageSize={pageSize => {
                        setFilters(old => ({
                            ...old,
                            limit: pageSize,
                            page: 0,
                        }));

                        navigation.setParams({
                            ...route.params,
                            limit: pageSize,
                            page: 1,
                        });
                    }}
                    setPage={page => {
                        setFilters(old => ({
                            ...old,
                            page
                        }));

                        navigation.setParams({
                            ...route.params,
                            page: page + 1
                        });
                    }}
                />}
            />
        </Card>
        <DeleteModal
            setModalVisible={setDeleteModalVisible}
            isModalVisible={isDeleteModalVisible}
            itemName={String(selectedActingBody?.name) || ''}
            onSubmit={async () => {
                if (selectedActingBody) {
                    await confirmDelete(selectedActingBody);
                }
            }}
        />
    </Container>;
}
